/**
 * Words API Client with React Query Hooks
 *
 * Provides query and mutation hooks for word management within word lists.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost, apiPut, apiDelete } from 'services/api';
import logger from 'utils/logger';
import type {
  Word,
  WordsInListResponse,
  WordExistsResponse,
  AllWordsResponse,
  AddWordToListRequest,
  WordUpdateRequest,
  ReorderWordsRequest,
  WordApiResponse,
  WordsInListApiResponse,
  WordExistsApiResponse,
  AllWordsApiResponse,
} from '../types/word.types';
import {
  mapWordFromApi,
  mapWordInListFromApi,
  mapWordWithCoverageFromApi,
} from '../types/word.types';
import { wordListsKeys } from './wordListsApi';

// Query keys
export const wordsKeys = {
  all: ['words'] as const,
  allPaginated: (page: number, pageSize: number, search?: string, languageCode?: string) =>
    [...wordsKeys.all, 'paginated', { page, pageSize, search, languageCode }] as const,
  inList: (listId: string) => [...wordsKeys.all, 'list', listId] as const,
  inListPaginated: (listId: string, page: number, pageSize: number) =>
    [...wordsKeys.inList(listId), { page, pageSize }] as const,
  detail: (word: string, languageCode: string, cardId?: string) =>
    [...wordsKeys.all, 'detail', word, languageCode, cardId] as const,
  exists: (word: string, languageCode: string) =>
    [...wordsKeys.all, 'exists', word, languageCode] as const,
};

// ============================================================================
// API Functions
// ============================================================================

interface ListAllWordsParams {
  page?: number;
  pageSize?: number;
  search?: string;
  languageCode?: string;
}

const fetchAllWords = async (params: ListAllWordsParams): Promise<AllWordsResponse> => {
  const queryParams: Record<string, string | number> = {};
  if (params.page) queryParams.page = params.page;
  if (params.pageSize) queryParams.limit = params.pageSize;
  if (params.search) queryParams.search = params.search;
  if (params.languageCode) queryParams.language_code = params.languageCode;

  const response = await apiGet<AllWordsApiResponse>('/api/admin/words', queryParams);

  return {
    items: response.items.map(mapWordWithCoverageFromApi),
    total: response.total,
    page: response.page,
    pageSize: response.page_size,
  };
};

interface ListWordsParams {
  listId: string;
  page?: number;
  pageSize?: number;
}

const fetchWordsInList = async (params: ListWordsParams): Promise<WordsInListResponse> => {
  const queryParams: Record<string, number> = {};
  if (params.page) queryParams.page = params.page;
  if (params.pageSize) queryParams.page_size = params.pageSize;

  const response = await apiGet<WordsInListApiResponse>(
    `/api/admin/word-lists/${params.listId}/words`,
    queryParams
  );

  return {
    items: response.items.map(mapWordInListFromApi),
    total: response.total,
    page: response.page,
    pageSize: response.page_size,
  };
};

const fetchWord = async (word: string, languageCode: string, cardId?: string): Promise<Word> => {
  const response = await apiGet<WordApiResponse>(
    `/api/admin/words/${encodeURIComponent(word)}/${languageCode}`,
    cardId ? { card_id: cardId } : undefined
  );
  return mapWordFromApi(response);
};

const checkWordExists = async (word: string, languageCode: string): Promise<WordExistsResponse> => {
  const response = await apiGet<WordExistsApiResponse>('/api/admin/words/check', {
    word,
    language_code: languageCode,
  });
  return {
    exists: response.exists,
    word: response.word ? mapWordFromApi(response.word) : null,
  };
};

const addWordToList = async (listId: string, data: AddWordToListRequest): Promise<Word> => {
  const response = await apiPost<WordApiResponse>(`/api/admin/word-lists/${listId}/words`, data);
  return mapWordFromApi(response);
};

const updateWord = async (
  word: string,
  languageCode: string,
  data: WordUpdateRequest,
  cardId?: string
): Promise<Word> => {
  const response = await apiPut<WordApiResponse>(
    `/api/admin/words/${encodeURIComponent(word)}/${languageCode}${
      cardId ? `?card_id=${encodeURIComponent(cardId)}` : ''
    }`,
    data
  );
  return mapWordFromApi(response);
};

const removeWordFromList = async (
  listId: string,
  word: string,
  languageCode: string,
  cardId?: string
): Promise<void> => {
  await apiDelete<void>(
    `/api/admin/word-lists/${listId}/words/${encodeURIComponent(word)}/${languageCode}${
      cardId ? `?card_id=${encodeURIComponent(cardId)}` : ''
    }`
  );
};

const reorderWordsInList = async (listId: string, data: ReorderWordsRequest): Promise<Word[]> => {
  const response = await apiPut<WordApiResponse[]>(
    `/api/admin/word-lists/${listId}/words/reorder`,
    data
  );
  return response.map(mapWordFromApi);
};

/**
 * Create a standalone word in the global word bank (no list association)
 */
const createWord = async (data: AddWordToListRequest): Promise<Word> => {
  const response = await apiPost<WordApiResponse>('/api/admin/words', data);
  return mapWordFromApi(response);
};

// ============================================================================
// React Query Hooks
// ============================================================================

/**
 * Fetch paginated all words (global word bank)
 */
export const useAllWords = (page = 1, pageSize = 20, search?: string, languageCode?: string) => {
  return useQuery({
    queryKey: wordsKeys.allPaginated(page, pageSize, search, languageCode),
    queryFn: () => fetchAllWords({ page, pageSize, search, languageCode }),
  });
};

/**
 * Fetch paginated words in a word list
 */
export const useWordsInList = (listId: string, page = 1, pageSize = 20) => {
  return useQuery({
    queryKey: wordsKeys.inListPaginated(listId, page, pageSize),
    queryFn: () => fetchWordsInList({ listId, page, pageSize }),
    enabled: !!listId,
  });
};

/**
 * Fetch a single word by its composite key
 */
export const useWord = (word: string, languageCode: string, cardId?: string) => {
  return useQuery({
    queryKey: wordsKeys.detail(word, languageCode, cardId),
    queryFn: () => fetchWord(word, languageCode, cardId),
    enabled: !!word && !!languageCode,
  });
};

/**
 * Check if a word exists in the database
 */
export const useCheckWordExists = (word: string, languageCode: string, enabled = true) => {
  return useQuery({
    queryKey: wordsKeys.exists(word, languageCode),
    queryFn: () => checkWordExists(word, languageCode),
    enabled: enabled && !!word && !!languageCode,
  });
};

/**
 * Add a word to a list (creates word if it doesn't exist)
 */
export const useAddWordToList = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ listId, data }: { listId: string; data: AddWordToListRequest }) =>
      addWordToList(listId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: wordsKeys.inList(variables.listId) });
      queryClient.invalidateQueries({ queryKey: wordListsKeys.detail(variables.listId) });
    },
    onError: (error) => {
      logger.error('Error adding word to list:', error);
    },
  });
};

/**
 * Update a word's metadata (global, applies across all lists)
 */
export const useUpdateWord = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      word,
      languageCode,
      cardId,
      data,
    }: {
      word: string;
      languageCode: string;
      cardId?: string;
      data: WordUpdateRequest;
    }) => updateWord(word, languageCode, data, cardId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: wordsKeys.detail(variables.word, variables.languageCode, variables.cardId),
      });
      // Invalidate all word lists since word might be in multiple lists
      queryClient.invalidateQueries({ queryKey: wordsKeys.all });
    },
    onError: (error) => {
      logger.error('Error updating word:', error);
    },
  });
};

/**
 * Remove a word from a list (word remains in database)
 */
export const useRemoveWordFromList = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      listId,
      word,
      languageCode,
      cardId,
    }: {
      listId: string;
      word: string;
      languageCode: string;
      cardId?: string;
    }) => removeWordFromList(listId, word, languageCode, cardId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: wordsKeys.inList(variables.listId) });
      queryClient.invalidateQueries({ queryKey: wordListsKeys.detail(variables.listId) });
    },
    onError: (error) => {
      logger.error('Error removing word from list:', error);
    },
  });
};

/**
 * Reorder words within a list
 */
export const useReorderWords = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ listId, data }: { listId: string; data: ReorderWordsRequest }) =>
      reorderWordsInList(listId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: wordsKeys.inList(variables.listId) });
    },
    onError: (error) => {
      logger.error('Error reordering words:', error);
    },
  });
};

/**
 * Create a standalone word in the global word bank (no list association)
 */
export const useCreateWord = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: AddWordToListRequest) => createWord(data),
    onSuccess: () => {
      // Invalidate all words query to refresh the list
      queryClient.invalidateQueries({ queryKey: wordsKeys.all });
    },
    onError: (error) => {
      logger.error('Error creating word:', error);
    },
  });
};
